import { useState, useCallback } from 'react';

const COLLECTION_STATES = {
  IDLE: 'idle',
  COLLECTING_NAME: 'collecting_name',
  COLLECTING_EMAIL: 'collecting_email',
  COLLECTING_TELEPHONE: 'collecting_telephone',
  COLLECTING_PROMOTIONAL: 'collecting_promotional',
  COMPLETED: 'completed'
};

/**
 * Custom hook to manage conversational user data collection
 * Handles state machine for asking questions one by one through chat
 */
const useConversationalUserData = () => {
  const [collectionState, setCollectionState] = useState(COLLECTION_STATES.IDLE);
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    telephone: '',
    acceptsPromotionalEmails: false
  });
  const [validationError, setValidationError] = useState(null);

  // Validation functions
  const validateName = (name) => {
    if (!name || !name.trim()) {
      return 'Please provide your name.';
    }
    if (name.trim().length < 2) {
      return 'Name must be at least 2 characters long.';
    }
    return null;
  };

  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      return 'Please provide your email address.';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please provide a valid email address (e.g., name@example.com).';
    }
    return null;
  };

  const validateTelephone = (telephone) => {
    if (!telephone || !telephone.trim()) {
      return 'Please provide your telephone number.';
    }
    // Remove common formatting characters
    const cleanedNumber = telephone.replace(/[\s\-()]/g, '');
    if (cleanedNumber.length < 8) {
      return 'Please provide a valid telephone number (at least 8 digits).';
    }
    return null;
  };

  // Start the collection process
  const startCollection = useCallback(() => {
    console.log('🎬 [UserData] Starting collection process');
    setCollectionState(COLLECTION_STATES.COLLECTING_NAME);
    setUserData({
      name: '',
      email: '',
      telephone: '',
      acceptsPromotionalEmails: false
    });
    setValidationError(null);
  }, []);

  // Get the current question to ask the user
  const getCurrentQuestion = useCallback((stateOverride = null, userDataOverride = null) => {
    const effectiveState = stateOverride || collectionState;
    const effectiveUserData = userDataOverride || userData;
    console.log('❓ [UserData] Getting current question for state:', effectiveState, stateOverride ? '(override)' : '(current)');
    console.log('❓ [UserData] Using userData:', effectiveUserData);

    let question;
    switch (effectiveState) {
      case COLLECTION_STATES.COLLECTING_NAME:
        question = {
          text: "👋 Welcome! To get started, what's your name?",
          field: 'name',
          type: 'text'
        };
        break;
      case COLLECTION_STATES.COLLECTING_EMAIL:
        question = {
          text: `Great to meet you, ${effectiveUserData.name}! What's your email address?`,
          field: 'email',
          type: 'text'
        };
        break;
      case COLLECTION_STATES.COLLECTING_TELEPHONE:
        question = {
          text: effectiveUserData.name ? `Perfect! And what's your telephone number, ${effectiveUserData.name}?` : "Perfect! What's your telephone number?",
          field: 'telephone',
          type: 'text'
        };
        break;
      case COLLECTION_STATES.COLLECTING_PROMOTIONAL:
        question = {
          text: "Last question - would you like to receive promotional emails about new features and special offers?",
          field: 'acceptsPromotionalEmails',
          type: 'options',
          options: [
            { id: 'yes', label: 'Yes, keep me updated', value: true },
            { id: 'no', label: 'No, thanks', value: false }
          ]
        };
        break;
      default:
        question = null;
    }

    console.log('❓ [UserData] Returning question:', question ? question.field : 'null');
    return question;
  }, [collectionState, userData]);

  // Process user's response
  const processResponse = useCallback((response) => {
    console.log('🔄 [UserData] Processing response:', response, 'in state:', collectionState);
    setValidationError(null);

    switch (collectionState) {
      case COLLECTION_STATES.COLLECTING_NAME: {
        console.log('✏️ [UserData] Validating name:', response);
        const error = validateName(response);
        if (error) {
          console.log('❌ [UserData] Name validation failed:', error);
          setValidationError(error);
          return { success: false, error, nextState: collectionState };
        }
        console.log('✅ [UserData] Name validated, moving to email');
        const updatedData = { ...userData, name: response.trim() };
        setUserData(updatedData);
        setCollectionState(COLLECTION_STATES.COLLECTING_EMAIL);
        return { success: true, nextState: COLLECTION_STATES.COLLECTING_EMAIL, updatedUserData: updatedData };
      }

      case COLLECTION_STATES.COLLECTING_EMAIL: {
        console.log('✏️ [UserData] Validating email:', response);
        const error = validateEmail(response);
        if (error) {
          console.log('❌ [UserData] Email validation failed:', error);
          setValidationError(error);
          return { success: false, error, nextState: collectionState };
        }
        console.log('✅ [UserData] Email validated, moving to telephone');
        const updatedData = { ...userData, email: response.trim().toLowerCase() };
        setUserData(updatedData);
        setCollectionState(COLLECTION_STATES.COLLECTING_TELEPHONE);
        return { success: true, nextState: COLLECTION_STATES.COLLECTING_TELEPHONE, updatedUserData: updatedData };
      }

      case COLLECTION_STATES.COLLECTING_TELEPHONE: {
        console.log('✏️ [UserData] Validating telephone:', response);
        const error = validateTelephone(response);
        if (error) {
          console.log('❌ [UserData] Telephone validation failed:', error);
          setValidationError(error);
          return { success: false, error, nextState: collectionState };
        }
        console.log('✅ [UserData] Telephone validated, moving to promotional');
        const updatedData = { ...userData, telephone: response.trim() };
        setUserData(updatedData);
        setCollectionState(COLLECTION_STATES.COLLECTING_PROMOTIONAL);
        return { success: true, nextState: COLLECTION_STATES.COLLECTING_PROMOTIONAL, updatedUserData: updatedData };
      }

      case COLLECTION_STATES.COLLECTING_PROMOTIONAL: {
        console.log('✏️ [UserData] Processing promotional choice:', response, 'Type:', typeof response);
        // Response should be boolean or 'yes'/'no' string
        const acceptsEmails = response === true ||
                             (typeof response === 'string' && response.toLowerCase() === 'yes');
        console.log('✅ [UserData] Collection completed! Accepts emails:', acceptsEmails);
        const updatedData = { ...userData, acceptsPromotionalEmails: acceptsEmails };
        setUserData(updatedData);
        setCollectionState(COLLECTION_STATES.COMPLETED);
        return {
          success: true,
          nextState: COLLECTION_STATES.COMPLETED,
          completed: true,
          updatedUserData: updatedData
        };
      }

      default:
        console.log('❌ [UserData] Invalid state:', collectionState);
        return { success: false, error: 'Invalid state' };
    }
  }, [collectionState, userData]);

  // Reset collection
  const resetCollection = useCallback(() => {
    setCollectionState(COLLECTION_STATES.IDLE);
    setUserData({
      name: '',
      email: '',
      telephone: '',
      acceptsPromotionalEmails: false
    });
    setValidationError(null);
  }, []);

  // Check if collection is active
  const isCollecting = collectionState !== COLLECTION_STATES.IDLE &&
                       collectionState !== COLLECTION_STATES.COMPLETED;

  // Check if collection is complete
  const isComplete = collectionState === COLLECTION_STATES.COMPLETED;

  // Get collected data formatted for API
  const getCollectedData = useCallback(() => {
    if (!isComplete) {
      return null;
    }
    return {
      name: userData.name,
      email: userData.email,
      telephone: userData.telephone,
      accepts_promotional_emails: userData.acceptsPromotionalEmails,
      source: 'boris_chat_widget_conversational',
      metadata: {
        widget_version: '1.0',
        entry_point: 'conversational_chat',
        collection_method: 'conversation',
        timestamp: new Date().toISOString()
      }
    };
  }, [isComplete, userData]);

  return {
    // State
    collectionState,
    userData,
    validationError,
    isCollecting,
    isComplete,

    // Actions
    startCollection,
    processResponse,
    getCurrentQuestion,
    getCollectedData,
    resetCollection,

    // Constants
    COLLECTION_STATES
  };
};

export default useConversationalUserData;
